// API client shared by the web UI and the extension.
//
// The two differ only in where the token lives (localStorage vs
// ext.storage.local, which is async) and what to do when it's rejected, so
// both are injected. Everything else — URL building, error shape, the 401/403
// contract — is identical and lives here.

import type {
  Annotation, AnnotationInput, ApiErrorBody, ArticleContent, AudiobookLinks, AuthSession, AuthUser, Bookmark, BookmarkInput,
  BookmarkUploadOptions, BulkInsertResult, FeedEpisodeSummary, Folder, Highlight, HighlightQuery, PendingVideo, ReadingState, ReadingStatus,
  SignUpResult, Tag, TranscriptSubmission,
} from './types.ts';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface CreateApiOptions {
  baseUrl: string;
  /** Sync in the web UI (localStorage), async in the extension (ext.storage). */
  getToken: () => string | null | Promise<string | null>;
  /** Called before an ApiError is thrown for a 401/403, so each client can
   *  clear its own storage and show its own login view. */
  onUnauthorized?: () => void | Promise<void>;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON-encoded, unless it's a Blob (a file upload), which is sent as-is. */
  body?: unknown;
  /** Return the response as text rather than parsed JSON. */
  raw?: boolean;
}

export interface BookmarkQuery {
  tag?: string | null;
  q?: string | null;
  status?: ReadingStatus | null;
  limit?: number | null;
  offset?: number | null;
}

/**
 * @param {object} options
 * @param {string} options.baseUrl
 * @param {() => string|null|Promise<string|null>} options.getToken
 * @param {() => void|Promise<void>} [options.onUnauthorized] called before the
 *        error is thrown when the server rejects the token, so each client can
 *        clear its own storage and show its own login view.
 */
export function createApi({ baseUrl, getToken, onUnauthorized }: CreateApiOptions) {
  async function request<T>(path: string, { method = 'GET', body, raw = false }: RequestOptions = {}): Promise<T> {
    const token = await getToken();
    const headers: Record<string, string> = {};
    const isFile = body instanceof Blob;
    if (body !== undefined) headers['Content-Type'] = isFile ? 'application/octet-stream' : 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : isFile ? body : JSON.stringify(body),
      });
    } catch (err) {
      // fetch only rejects on network failure; give that a recognizable shape
      // so callers can tell "offline" from "server said no".
      throw new ApiError((err as Error).message || 'Network request failed', 0);
    }

    if (res.status === 401 || res.status === 403) {
      if (onUnauthorized) await onUnauthorized();
      throw new ApiError('Session expired — please log in again', res.status);
    }

    if (!res.ok) {
      const payload = (await res.json().catch(() => ({}))) as Partial<ApiErrorBody>;
      throw new ApiError(payload.error || `HTTP ${res.status}`, res.status);
    }

    if (raw) return (await res.text()) as T;
    if (res.status === 204) return null as T;
    return (await res.json()) as T;
  }

  // Sign-in and the rest of /auth are served by Better Auth, which differs from
  // the rest of the API in three ways that keep these calls out of `request`:
  // the session token comes back in a response header, errors are
  // `{ code, message }` rather than `{ error }`, and a 401 means "wrong
  // password" — not "your session expired" — so it must not fire onUnauthorized.
  async function authCall<T>(
    path: string,
    body: unknown,
    { token }: { token?: string | null } = {},
  ): Promise<{ data: T; token: string | null }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${baseUrl}/auth${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
    } catch (err) {
      throw new ApiError((err as Error).message || 'Network request failed', 0);
    }

    const payload = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    if (!res.ok) throw new ApiError(payload.message || payload.error || `HTTP ${res.status}`, res.status);
    return { data: payload as T, token: res.headers.get('set-auth-token') };
  }

  // Generic over any flat param bag (BookmarkQuery, HighlightQuery, …) — it
  // only ever reads and stringifies entries, never anything type-specific.
  // A plain `object` parameter (rather than Record<string, ...>) sidesteps
  // TS requiring an explicit index signature on every query-params interface.
  function query(params?: object): string {
    const qs = new URLSearchParams();
    const entries = Object.entries(params ?? {}) as [string, string | number | null | undefined][];
    for (const [key, value] of entries) {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  return {
    baseUrl,
    request,

    // Auth. Callers keep the token themselves (see getToken).
    signIn: async (username: string, password: string): Promise<AuthSession> => {
      const { data, token } = await authCall<{ user: AuthUser }>('/sign-in/username', { username, password });
      if (!token) throw new ApiError('The server did not return a session token', 500);
      return { token, user: data.user };
    },
    signUp: async (fields: { username: string; email: string; password: string }): Promise<SignUpResult> => {
      const { data, token } = await authCall<{ user: AuthUser }>('/sign-up/email', { ...fields, name: fields.username });
      return { token, user: data.user };
    },
    /** Revokes the session server-side. Best-effort: signing out locally must not depend on the network. */
    signOut: async (): Promise<void> => {
      const token = await getToken();
      if (token) await authCall('/sign-out', {}, { token }).catch(() => {});
    },
    requestPasswordReset: async (email: string, redirectTo: string): Promise<void> => {
      await authCall('/request-password-reset', { email, redirectTo });
    },
    resetPassword: async (token: string, newPassword: string): Promise<void> => {
      await authCall('/reset-password', { token, newPassword });
    },

    // Bookmarks
    getBookmarks: (params?: BookmarkQuery) => request<Bookmark[]>(`/bookmarks${query(params)}`),
    getBookmark: (id: number) => request<Bookmark>(`/bookmarks/${id}`),
    addBookmark: (bookmark: BookmarkInput) => request<Bookmark>('/bookmarks', { method: 'POST', body: bookmark }),
    addBookmarks: (bookmarks: BookmarkInput[]) =>
      request<BulkInsertResult>('/bookmarks', { method: 'POST', body: bookmarks }),
    updateBookmark: (id: number, changes: Partial<BookmarkInput>) =>
      request<Bookmark>(`/bookmarks/${id}`, { method: 'PATCH', body: changes }),
    /** Save a file (pdf, epub, txt, markdown, or an m4b/m4a/mp3 audiobook) as a bookmark. */
    uploadFile: (file: File, { title, tags }: BookmarkUploadOptions = {}) =>
      request<Bookmark>(
        `/bookmarks/upload${query({ filename: file.name, title, tags: tags?.join(',') })}`,
        { method: 'POST', body: file },
      ),
    /** Moves it to the trash, where it can be restored for 30 days. */
    deleteBookmark: (id: number) => request<null>(`/bookmarks/${id}`, { method: 'DELETE' }),

    // Trash: deleted bookmarks, most recent first, each with deleted_at and purge_at
    getTrash: () => request<Bookmark[]>('/trash'),
    restoreBookmark: (id: number) => request<Bookmark>(`/trash/${id}/restore`, { method: 'POST' }),
    /** Deletes a trashed bookmark for good, with its article, highlights and files. */
    deleteForever: (id: number) => request<null>(`/trash/${id}`, { method: 'DELETE' }),
    emptyTrash: () => request<null>('/trash', { method: 'DELETE' }),

    // Tags
    getTags: () => request<Tag[]>('/tags'),

    // Folders. A bookmark is moved with updateBookmark(id, { folder_id }), or
    // several at once with moveBookmarks; null is the top level.
    getFolders: () => request<Folder[]>('/folders'),
    createFolder: (name: string) => request<Folder>('/folders', { method: 'POST', body: { name } }),
    renameFolder: (id: number, name: string) =>
      request<Folder>(`/folders/${id}`, { method: 'PATCH', body: { name } }),
    reorderFolders: (ids: number[]) => request<Folder[]>('/folders/order', { method: 'PUT', body: { ids } }),
    deleteFolder: (id: number) => request<null>(`/folders/${id}`, { method: 'DELETE' }),
    moveBookmarks: (ids: number[], folderId: number | null) =>
      request<{ moved: number }>('/bookmarks/move', { method: 'POST', body: { ids, folder_id: folderId } }),

    // Reading
    getContent: (id: number) => request<ArticleContent>(`/bookmarks/${id}/content`),
    /** Signed links to an audiobook's audio and cover, for an <audio> or <img>
     *  to load without a token. Paths, relative to the API. */
    getAudiobookLinks: (id: number) => request<AudiobookLinks>(`/bookmarks/${id}/audiobook`),
    getMarkdown: (id: number) => request<string>(`/bookmarks/${id}/content?format=md`, { raw: true }),
    archive: (id: number, payload: { html?: string; base_url?: string } = {}) =>
      request<Bookmark>(`/bookmarks/${id}/archive`, { method: 'POST', body: payload }),
    deleteArchive: (id: number) => request<null>(`/bookmarks/${id}/archive`, { method: 'DELETE' }),
    setReadingState: (id: number, state: { scroll_ratio?: number; status?: ReadingStatus }) =>
      request<ReadingState>(`/bookmarks/${id}/reading-state`, { method: 'PUT', body: state }),
    getAudioSentences: (id: number) => request<{ sentences: string[] }>(`/bookmarks/${id}/audio/sentences`),

    // A podcast show's episodes, and saving one as a bookmark of its own
    getEpisodes: (id: number) => request<FeedEpisodeSummary[]>(`/bookmarks/${id}/episodes`),
    saveEpisode: (id: number, key: string) =>
      request<Bookmark>(`/bookmarks/${id}/episodes`, { method: 'POST', body: { key } }),

    // Video transcripts the server couldn't fetch, which the extension fetches instead
    getPendingVideos: () => request<PendingVideo[]>('/youtube/pending'),
    submitTranscript: (id: number, submission: TranscriptSubmission) =>
      request<Bookmark>(`/bookmarks/${id}/transcript`, { method: 'POST', body: submission }),

    // Annotations
    getAnnotations: (id: number) => request<Annotation[]>(`/bookmarks/${id}/annotations`),
    getHighlights: (params?: HighlightQuery) => request<Highlight[]>(`/annotations${query(params)}`),
    addAnnotation: (id: number, annotation: AnnotationInput) =>
      request<Annotation>(`/bookmarks/${id}/annotations`, { method: 'POST', body: annotation }),
    updateAnnotation: (id: number, changes: { comment?: string | null; color?: string | null }) =>
      request<Annotation>(`/annotations/${id}`, { method: 'PATCH', body: changes }),
    deleteAnnotation: (id: number) => request<null>(`/annotations/${id}`, { method: 'DELETE' }),
  };
}
